import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { AreaId } from "../../content/map";
import { AREA_BY_ID } from "../../content/map";

type Props = {
  areaId: AreaId;
  onFinish: () => void;
};

interface AreaCutsceneTheme {
  title: string;
  catchphrase: string;
  badge: string;
  bgGradient: string;
  mainEmoji: string;
  subEmojis: string[];
  soundCue: string;
  actionScript: string;
}

const CUTSCENE_THEMES: Record<AreaId, AreaCutsceneTheme> = {
  airport: {
    title: "関西国際空港",
    catchphrase: "飛行機が滑走路に着陸！日本語の旅がいま始まるで！",
    badge: "STAGE 01 • 到着",
    bgGradient: "from-sky-700 via-blue-600 to-amber-500",
    mainEmoji: "✈️",
    subEmojis: ["🧳", "🛫", "🛬", "🌉", "🎫"],
    soundCue: "キーン… ゴォォォォ！",
    actionScript: "カモメ船長「ようこそ関西へ！長旅おつかれさまやで〜！」",
  },
  home: {
    title: "シェアハウス",
    catchphrase: "ただいま！今日からここがあなたの新しい拠点！",
    badge: "STAGE 02 • 新生活",
    bgGradient: "from-amber-600 via-orange-500 to-rose-400",
    mainEmoji: "🏠",
    subEmojis: ["🔑", "📦", "🪑", "⏰", "🍵"],
    soundCue: "ガチャ… カランコロン♪",
    actionScript: "コアラ大家「ようこそ！荷ほどきが終わったらお茶でも飲んでや」",
  },
  school: {
    title: "日本語学校",
    catchphrase: "キンコンカンコン…！仲間と一緒に新しい言葉を学ぼう！",
    badge: "STAGE 03 • 学び",
    bgGradient: "from-blue-800 via-indigo-700 to-orange-500",
    mainEmoji: "🏫",
    subEmojis: ["📚", "✏️", "🔔", "📖", "🎓"],
    soundCue: "キンコンカンコン…♪",
    actionScript: "フクロウ先生「みなさんおはようございます！楽しく学びましょうホー」",
  },
  shop: {
    title: "街角コンビニ",
    catchphrase: "ピロリロリ〜ン♪ 温かいお弁当と笑顔でお出迎え！",
    badge: "STAGE 04 • お買い物",
    bgGradient: "from-emerald-700 via-teal-600 to-orange-500",
    mainEmoji: "🏪",
    subEmojis: ["🍙", "🍱", "🥤", "🏮", "✨"],
    soundCue: "入店チャイム♪",
    actionScript: "タヌキ店長「いらっしゃいませー！温めますポン？」",
  },
  cafe: {
    title: "中崎町レトロカフェ",
    catchphrase: "コポコポ…芳醇なコーヒーと甘いスイーツのひととき",
    badge: "STAGE 05 • 注文",
    bgGradient: "from-amber-900 via-stone-800 to-orange-800",
    mainEmoji: "☕",
    subEmojis: ["🍰", "🥐", "📻", "🪑", "🐱"],
    soundCue: "カランコロン… ドリップの音♪",
    actionScript: "ネコバリスタ「ニャー、深煎りコーヒー淹れたてニャ。ゆっくりしてってニャ」",
  },
  station: {
    title: "なんば駅",
    catchphrase: "ガタゴト…電車が滑り込む！次の街へ出発進行！",
    badge: "STAGE 06 • 交通",
    bgGradient: "from-amber-700 via-yellow-600 to-blue-900",
    mainEmoji: "🚃",
    subEmojis: ["🎫", "🚉", "🧭", "🛤️", "🏙️"],
    soundCue: "プシュー… 発車ベル♪",
    actionScript: "シバ駅長「ワン！2番線に電車がまいります！白線の内側へワン！」",
  },
  library: {
    title: "中之島図書館",
    catchphrase: "静かな読書の空間。知恵と言葉の宝庫を巡ろう",
    badge: "STAGE 07 • 探求",
    bgGradient: "from-slate-800 via-teal-800 to-blue-900",
    mainEmoji: "📚",
    subEmojis: ["📖", "👓", "🏛️", "🔖", "🕯️"],
    soundCue: "パラパラ… ページをめくる音♪",
    actionScript: "ヒツジ司書「メェ〜、調べものなら何でも聞いてくださいね」",
  },
  park: {
    title: "扇町公園",
    catchphrase: "青空の下、芝生でピクニック！のびのびリフレッシュ",
    badge: "STAGE 08 • 憩い",
    bgGradient: "from-emerald-700 via-green-600 to-yellow-500",
    mainEmoji: "🌳",
    subEmojis: ["🌸", "⛲", "🥪", "🕊️", "🎈"],
    soundCue: "サワサワ… そよ風と小鳥の声♪",
    actionScript: "リスランナー「いい天気だね！一緒に一周走ろう！」",
  },
  friendhouse: {
    title: "ともだちの家",
    catchphrase: "お邪魔します！手土産持っておしゃべりタイム",
    badge: "STAGE 09 • 交流",
    bgGradient: "from-rose-700 via-orange-600 to-amber-500",
    mainEmoji: "🛋️",
    subEmojis: ["🎁", "🎮", "🍵", "🖼️", "🍰"],
    soundCue: "ピンポーン♪「はーい！」",
    actionScript: "クマともだち「来てくれて嬉しいよ！ゲームしよう！」",
  },
  hospital: {
    title: "扇町クリニック",
    catchphrase: "もしもの時も安心。症状をしっかり伝えよう",
    badge: "STAGE 10 • 安心",
    bgGradient: "from-sky-800 via-teal-700 to-slate-600",
    mainEmoji: "🏥",
    subEmojis: ["🩺", "💊", "🩹", "📋", "🌡️"],
    soundCue: "「次の方、診察室へどうぞ」♪",
    actionScript: "シカ医師「お大事に。どこが痛むか教えてくださいね」",
  },
  cityoffice: {
    title: "北区役所",
    catchphrase: "住民登録から手続きまで、暮らしのサポート窓口",
    badge: "STAGE 11 • 手続き",
    bgGradient: "from-indigo-900 via-blue-800 to-slate-700",
    mainEmoji: "🏢",
    subEmojis: ["📄", "🏷️", "🖊️", "💼", "🔖"],
    soundCue: "「ピンポーン、34番の番号札をお持ちの方」♪",
    actionScript: "ペンギン係員「パタパタ！手続きはお任せくださいッス！」",
  },
  workplace: {
    title: "アルバイト先",
    catchphrase: "タイムカードを押して勤務開始！実践日本語の舞台！",
    badge: "STAGE 12 • 実践",
    bgGradient: "from-purple-950 via-indigo-900 to-amber-600",
    mainEmoji: "💼",
    subEmojis: ["⏱️", "👔", "📝", "🍵", "✨"],
    soundCue: "ピッ！「おはようございます！」♪",
    actionScript: "カワウソ店長「今日も元気にがんばりましょう！よろしくお願いします！」",
  },
};

export function AreaOpeningCutscene({ areaId, onFinish }: Props) {
  const theme = CUTSCENE_THEMES[areaId] || CUTSCENE_THEMES.airport;
  const mapArea = AREA_BY_ID[areaId];
  const [step, setStep] = useState<number>(0);

  useEffect(() => {
    // タイムラインシーケンス
    const t1 = setTimeout(() => setStep(1), 400);  // テーマタイトル
    const t2 = setTimeout(() => setStep(2), 1600); // 住民セリフ＆キャッチフレーズ
    const t3 = setTimeout(() => {
      onFinish();
    }, 4200); // 自動終了

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [onFinish]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black select-none"
      >
        {/* 背景グラデーション ＆ ダイナミックシネマ効果 */}
        <motion.div
          animate={{
            scale: [1, 1.08, 1.04],
            rotate: [0, 0.5, 0],
          }}
          transition={{ duration: 4.2, ease: "easeOut" }}
          className={`absolute inset-0 bg-gradient-to-br ${theme.bgGradient} opacity-90`}
        />

        {/* 映画風上下レターボックス */}
        <div className="absolute top-0 left-0 right-0 h-10 sm:h-14 bg-black z-30 shadow-lg" />
        <div className="absolute bottom-0 left-0 right-0 h-10 sm:h-14 bg-black z-30 shadow-lg" />

        {/* 舞い散るサブ絵文字パーティクル */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
          {theme.subEmojis.map((emoji, idx) => (
            <motion.span
              key={idx}
              initial={{
                opacity: 0,
                x: `${15 + idx * 18}vw`,
                y: "110vh",
                scale: 0.8,
              }}
              animate={{
                opacity: [0, 0.85, 0],
                y: "-10vh",
                rotate: [0, 360],
                scale: [0.8, 1.3, 1],
              }}
              transition={{
                duration: 3.2,
                delay: 0.3 + idx * 0.35,
                ease: "easeInOut",
                repeat: Infinity,
              }}
              className="absolute text-3xl sm:text-5xl"
            >
              {emoji}
            </motion.span>
          ))}
        </div>

        {/* スキップボタン */}
        <button
          onClick={onFinish}
          className="absolute top-12 sm:top-16 right-5 sm:right-8 z-40 bg-white/20 hover:bg-white/30 backdrop-blur-md text-white text-xs sm:text-sm font-black px-4 py-2 rounded-full border border-white/40 transition active:scale-95 flex items-center gap-1.5 shadow-lg"
        >
          <span>スキップ</span>
          <span>⏩</span>
        </button>

        {/* メインコンテンツ */}
        <div className="relative z-20 max-w-xl mx-auto px-6 text-center text-white flex flex-col items-center">
          {/* ステージバッジ */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block bg-white/25 backdrop-blur-md px-4 py-1 rounded-full text-xs font-black tracking-wider uppercase border border-white/40 mb-3 shadow-md"
          >
            {theme.badge}
          </motion.div>

          {/* メインアイコン（躍動） */}
          <motion.div
            initial={{ scale: 0, rotate: -25 }}
            animate={{ scale: [0, 1.25, 1], rotate: [0, 5, 0] }}
            transition={{ duration: 0.9, type: "spring", bounce: 0.5 }}
            className="w-24 h-24 sm:w-32 sm:h-32 rounded-3xl bg-white/15 backdrop-blur-xl border-2 border-white/50 flex items-center justify-center text-5xl sm:text-7xl shadow-2xl mb-4"
          >
            <motion.span
              animate={{
                y: [0, -8, 0],
                rotate: [0, 6, -6, 0],
              }}
              transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
            >
              {theme.mainEmoji}
            </motion.span>
          </motion.div>

          {/* タイトル */}
          <motion.h1
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight drop-shadow-lg mb-2"
          >
            {theme.title}
          </motion.h1>

          {/* サウンドキュー / 効果音バッジ */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.9 }}
            transition={{ delay: 0.6 }}
            className="text-[11px] sm:text-xs text-amber-200 font-bold mb-3 tracking-wide"
          >
            ♪ {theme.soundCue}
          </motion.div>

          {/* キャッチコピー */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="text-sm sm:text-lg text-white/95 font-bold leading-relaxed max-w-md drop-shadow"
          >
            {theme.catchphrase}
          </motion.p>

          {/* 住民のアクションセリフ（ステップ2） */}
          <AnimatePresence>
            {step >= 2 && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: "spring", damping: 15 }}
                className="mt-6 bg-[#fffdf7]/95 text-stone-900 px-5 py-3 rounded-2xl border-2 border-[#d9b45f] shadow-2xl flex items-center gap-3 max-w-md"
              >
                <span className="text-2xl sm:text-3xl shrink-0">
                  {mapArea?.residentEmoji || "🐾"}
                </span>
                <p className="text-xs sm:text-sm font-black text-[#8f332c] text-left leading-snug">
                  {theme.actionScript}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
